<template>
  <div>
    <div v-if="loading" class="state-msg">{{ $t('bo.common.loading') }}</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>
    <div v-else-if="groups.length === 0" class="state-msg">{{ $t('bo.occupationSkills.noGroups') }}</div>

    <div v-else class="groups-list">
      <div v-for="group in groups" :key="group.id" class="group-card">
        <!-- Header groupe L1 -->
        <div class="group-header" @click="toggleGroup(group.id)">
          <div class="group-info">
            <span class="level-badge l1">L1</span>
            <span class="group-name">{{ group.name }}</span>
            <span class="count-badge">{{ $t('bo.occupationSkills.groupCountBadge', { subfamilies: group.subfamilies_count, roles: group.roles_count }) }}</span>
          </div>
          <span class="chevron">{{ expandedGroups.has(group.id) ? '▼' : '▶' }}</span>
        </div>

        <!-- Sous-familles L2 -->
        <div v-if="expandedGroups.has(group.id)" class="subfamilies">
          <div v-if="loadingGroup === group.id" class="state-msg-sm">{{ $t('bo.common.loading') }}</div>
          <template v-else-if="groupData[group.id]">
            <div v-for="sf in groupData[group.id].subfamilies" :key="sf.id" class="subfamily-block">
              <div class="subfamily-header" @click="toggleSubfamily(sf.id)">
                <span class="level-badge l2">L2</span>
                <span class="sf-name">{{ sf.name }}</span>
                <span class="count-badge">{{ $t('bo.occupationSkills.sfRolesCount', { count: sf.roles_count }) }}</span>
                <span class="chevron-sm">{{ expandedSubfamilies.has(sf.id) ? '▼' : '▶' }}</span>
              </div>

              <!-- Rôles L3 -->
              <div v-if="expandedSubfamilies.has(sf.id)" class="roles-list">
                <div v-for="role in sf.roles" :key="role.id" class="role-row">
                  <span class="level-badge l3">L3</span>
                  <span class="role-name">{{ role.name }}</span>
                  <span v-if="role.is_default_suggestion" class="badge-default">default</span>
                  <button @click="openRole(role.id)" class="btn-detail">{{ $t('bo.occupationSkills.viewDetail') }}</button>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Modal détail rôle -->
    <EditOccupationRoleModal
      v-if="editingRole"
      :role="editingRole"
      @close="editingRole = null"
      @updated="handleRoleUpdated"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import occupationSkillService from '@/services/occupationSkillService'

const { t } = useI18n()
import EditOccupationRoleModal from './EditOccupationRoleModal.vue'

const groups = ref([])
const loading = ref(false)
const error = ref('')
const loadingGroup = ref(null)
const groupData = ref({})
const expandedGroups = ref(new Set())
const expandedSubfamilies = ref(new Set())
const editingRole = ref(null)

const loadGroups = async () => {
  loading.value = true
  error.value = ''
  try {
    const res = await occupationSkillService.getGroups()
    groups.value = res.data || []
  } catch (e) {
    error.value = t('bo.occupationSkills.failedLoadGroups')
  } finally {
    loading.value = false
  }
}

const toggleGroup = async (groupId) => {
  if (expandedGroups.value.has(groupId)) {
    expandedGroups.value.delete(groupId)
    expandedGroups.value = new Set(expandedGroups.value)
    return
  }
  expandedGroups.value = new Set([...expandedGroups.value, groupId])
  if (!groupData.value[groupId]) {
    loadingGroup.value = groupId
    try {
      const res = await occupationSkillService.getGroupSubfamilies(groupId)
      groupData.value = { ...groupData.value, [groupId]: res.data }
    } catch (e) {
      error.value = t('bo.occupationSkills.failedLoadSubfamilies')
    } finally {
      loadingGroup.value = null
    }
  }
}

const toggleSubfamily = (sfId) => {
  if (expandedSubfamilies.value.has(sfId)) {
    expandedSubfamilies.value.delete(sfId)
  } else {
    expandedSubfamilies.value.add(sfId)
  }
  expandedSubfamilies.value = new Set(expandedSubfamilies.value)
}

const openRole = async (roleId) => {
  try {
    const res = await occupationSkillService.getRole(roleId)
    editingRole.value = res.data
  } catch (e) {
    alert(t('bo.occupationSkills.failedLoadRole'))
  }
}

const handleRoleUpdated = () => {
  editingRole.value = null
}

onMounted(loadGroups)
</script>

<style scoped>
.state-msg {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
}
.state-msg-sm { padding: 0.75rem 1rem; color: #6b7280; font-size: 0.8rem; }
.error-msg {
  padding: 1rem;
  color: #dc2626;
  background: #fef2f2;
  border-radius: 0.5rem;
  border: 1px solid #fecaca;
}

.groups-list { display: flex; flex-direction: column; gap: 0.75rem; }

.group-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  overflow: hidden;
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1.25rem;
  cursor: pointer;
  background: #f9fafb;
  user-select: none;
}
.group-header:hover { background: #f3f4f6; }

.group-info { display: flex; align-items: center; gap: 0.75rem; }
.group-name { font-weight: 700; color: #1f2937; font-size: 0.95rem; }
.chevron { color: #9ca3af; font-size: 0.75rem; }
.chevron-sm { color: #9ca3af; font-size: 0.65rem; margin-left: auto; }

.subfamilies { padding: 0.5rem 1rem 0.75rem; }

.subfamily-block { margin-bottom: 0.5rem; }
.subfamily-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-radius: 0.375rem;
  user-select: none;
}
.subfamily-header:hover { background: #f9fafb; }
.sf-name { font-weight: 600; color: #374151; font-size: 0.875rem; }

.roles-list {
  padding: 0.25rem 0 0.25rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.role-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.5rem;
  border-radius: 0.25rem;
}
.role-row:hover { background: #f9fafb; }
.role-name { font-size: 0.875rem; color: #374151; flex: 1; }

.level-badge {
  display: inline-block;
  padding: 0.1rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.l1 { background: #dbeafe; color: #1d4ed8; }
.l2 { background: #ede9fe; color: #6d28d9; }
.l3 { background: #d1fae5; color: #065f46; }

.count-badge {
  font-size: 0.75rem;
  color: #9ca3af;
  background: #f3f4f6;
  padding: 0.1rem 0.5rem;
  border-radius: 9999px;
}
.badge-default {
  font-size: 0.65rem;
  background: #fef3c7;
  color: #92400e;
  padding: 0.1rem 0.4rem;
  border-radius: 9999px;
  font-weight: 600;
}

.btn-detail {
  padding: 0.2rem 0.6rem;
  border: 1px solid #3b82f6;
  color: #3b82f6;
  background: white;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  cursor: pointer;
}
.btn-detail:hover { background: #eff6ff; }
</style>
